// useRole hook for role-based access control
// Reads role from JWT and returns permissions object

import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to get current user's role and permissions
 * @returns {object} { role, permissions, hasPermission }
 */
export function useRole() {
  const { user } = useAuth();

  const role = user?.role || 'org_viewer';

  // Define permissions for each role
  const rolePermissions = {
    plt_admin: {
      name: 'Platform Admin',
      canFilterByClient: true,
      canViewAlerts: true,
      canCreateRequest: true,
      canExport: true,
      canSeePII: true, // Requester column
      canSeeFlags: true,
      canOpenEdit: true,
      canSeeJurisdictionWidget: true,
      canSeeCompliancePanel: true,
      compliancePanelMode: 'advisory', // 'advisory' or 'operational'
      canAccessSettings: true
    },
    dpo: {
      name: 'DPO',
      canFilterByClient: true,
      canViewAlerts: true,
      canCreateRequest: true,
      canExport: true,
      canSeePII: true,
      canSeeFlags: true,
      canOpenEdit: true,
      canSeeJurisdictionWidget: true,
      canSeeCompliancePanel: true,
      compliancePanelMode: 'advisory',
      canAccessSettings: false
    },
    org_admin: {
      name: 'Organisation Admin',
      canFilterByClient: false, // Org-scoped already
      canViewAlerts: true,
      canCreateRequest: true,
      canExport: true,
      canSeePII: true,
      canSeeFlags: true,
      canOpenEdit: true,
      canSeeJurisdictionWidget: false,
      canSeeCompliancePanel: true,
      compliancePanelMode: 'operational', // Show as operational instructions
      canAccessSettings: false
    },
    org_user: {
      name: 'Organisation User',
      canFilterByClient: false,
      canViewAlerts: true,
      canCreateRequest: true,
      canExport: false,
      canSeePII: true,
      canSeeFlags: true,
      canOpenEdit: true,
      canSeeJurisdictionWidget: false,
      canSeeCompliancePanel: true,
      compliancePanelMode: 'operational',
      canAccessSettings: false
    },
    org_viewer: {
      name: 'Organisation Viewer',
      canFilterByClient: false,
      canViewAlerts: false,
      canCreateRequest: false,
      canExport: false, // Disabled, not hidden
      canSeePII: false, // Hide Requester column
      canSeeFlags: false,
      canOpenEdit: false, // Read-only link
      canSeeJurisdictionWidget: false,
      canSeeCompliancePanel: false,
      canAccessSettings: false
    }
  };

  const permissions = rolePermissions[role] || rolePermissions.org_viewer;

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission key to check
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    return permissions[permission] === true;
  };

  return {
    role,
    permissions,
    hasPermission,
    isAdmin: role === 'plt_admin',
    isDPO: role === 'dpo',
    isOrgAdmin: role === 'org_admin',
    isOrgUser: role === 'org_user',
    isViewer: role === 'org_viewer'
  };
}

export default useRole;
